<template>
<!--copy from vue-gallery-slideshow and edit insert caption -->
  <transition name="modal">
    <div
      v-if="imgIndex !== null"
      class="vgs"
      @click="close"
    >
      <button
        type="button"
        class="vgs__close"
        @click="close"
      >
        <!-- &times; -->
        <XIcon class="w-10 h-10" />
      </button>
      <button
        v-if="isMultiple"
        type="button"
        class="vgs__prev"
        @click.stop="onPrev"
      >
        <!-- &lsaquo; -->
        <ArrowLeftIcon class="w-6 h-6" />
      </button>
      <div
        v-if="images"
        class="vgs__container"
        @click.stop="onNext"
      >
        <img
          class="vgs__container__img"
          :src="imageUrl"
          :alt="alt"
          @click.stop="onNext"
        >
        <div class="caption" v-if="caption">
            <span >{{caption}}</span>
        </div>
        <slot></slot>
      </div>
      <button
        v-if="isMultiple"
        type="button"
        class="vgs__next"
        @click.stop="onNext"
      >
        <!-- &rsaquo; -->
        <ArrowRightIcon class="w-6 h-6" />
      </button>
      <div
        v-if="isMultiple||isOneImage"
        ref="gallery"
        class="vgs__gallery"
      >
        <div
          v-if="images"
          class="vgs__gallery__title"
        >
          {{ imgIndex + 1 }} / {{ images.length }}
        </div>
        <div
          v-if="images"
          class="vgs__gallery__container"
          :style="{ transform: 'translate(' + galleryXPos + 'px, 0)' }"
        >
          <img
            v-for="(img, i) in images"
            :key="i"
            class="vgs__gallery__container__img"
            :src="typeof img === 'string' ? img : img.image_url"
            :class="{ 'vgs__gallery__container__img--active': i === imgIndex}"
            :alt="typeof img === 'string' ? '' : img.alt"
            @click.stop="onClickThumb(img, i)"
          >
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from "vue"


const props = defineProps({
  images : {
    type: Array,
    required: true
  },
  index: {
    type: Number,
    required: false,
    default: null
  },
})
const emit = defineEmits(["close"])
const imgIndex = ref(props.index)
const isOneImage = ref(true)
const galleryXPos = ref(0)
const thumbnailWidth = ref(120)

onMounted(()=>{
  window.addEventListener("keydown", e => {
    if (e.keyCode === 37) {
      onPrev();
    } else if (e.keyCode === 39) {
      onNext();
    } else if (e.keyCode === 27) {
      close();
    }
  });
})

const imageUrl = computed(()=>{
  const img = props.images[imgIndex.value];
  if (typeof img === "string") {
      return img;
  }
  return img.image_url;
})
const alt = computed(()=>{
  const img = props.images[imgIndex.value];
  if (typeof img === "object") {
      return img.alt;
  }
  return "";
})
const isMultiple = computed(()=>{
  if(props.images.length > 1){
    return true
  }else{
    return false;
  }
})
const caption = computed(()=>{
  const img = props.images[imgIndex.value];
  return img.description
})
const onPrev = ()=>{
  if (imgIndex.value === null) return;
  if (imgIndex.value > 0) {
    imgIndex.value--;
  } else {
    imgIndex.value = props.images.length - 1;
  }
  updateThumbnails();
}
const onNext = () =>{
  if (imgIndex.value === null) return;
  if (imgIndex.value < props.images.length - 1) {
    imgIndex.value++;
  } else {
    imgIndex.value = 0;
  }
  updateThumbnails();

}
const close = () =>{
  imgIndex.value = null;
  emit("close", {imgIndex: imgIndex.value});
}
const onClickThumb = (image, index) => {
  imgIndex.value = index;
  updateThumbnails();
}
const updateThumbnails = () =>{
  if(!document.getElementsByClassName("vgs__gallery")[0])
    return;
  const galleryWidth = document.getElementsByClassName("vgs__gallery")[0].clientWidth;
  const currThumbsWidth = imgIndex.value * thumbnailWidth.value;
  const maxThumbsWidth = props.images.length * thumbnailWidth.value;
  const centerPos = Math.floor(galleryWidth / (thumbnailWidth.value * 2)) * thumbnailWidth.value;
  // Prevent scrolling of images if not needed
  if (maxThumbsWidth < galleryWidth) {
    return;
  }
  if (currThumbsWidth < centerPos) {
    galleryXPos.value = 0;
  } else if (currThumbsWidth > props.images.length * thumbnailWidth.value - galleryWidth + centerPos) {
    galleryXPos.value = -(props.images.length * thumbnailWidth.value - galleryWidth - 20);
  } else {
    galleryXPos.value = -(imgIndex.value * thumbnailWidth.value) + centerPos;
  }
}
//when click from another image
watch(
  () => props.index,
  (val, prev) => {
    imgIndex.value = val;
    // updateThumbnails when popup
    if (prev == null && val != null) {
        nextTick(()=>{
          updateThumbnails()
        })
    }
  }
)

</script>

<style lang="scss">
$black-alpha-80: rgba(0, 0, 0, 0.8);
$black: #000;
$white: #fff;
$radius-medium: 8px;
$radius-large: 12px;
.vgs {
  transition: opacity 0.2s ease;
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  min-height: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.85);
  display: table;
  &__close {
    color: #fff;
    position: absolute;
    top: 0;
    right: 0;
    background-color: transparent;
    border: none;
    font-size: 50px;
    width: 50px;
    height: 50px;
    cursor: pointer;
    z-index: 999;
    display: flex;
    justify-content: center;
    align-items: center;
    &:focus {
      outline: 0;
    }
  }
  &__prev,
  &__next {
    position: absolute;
    top: 50%;
    margin-top: -25px;
    width: 40px;
    height: 40px;
    z-index: 999;
    cursor: pointer;
    font-size: 5rem;
    color: #fff;
    background-color: transparent;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #858585;
    &:focus {
      outline: 0;
    }
    @media (max-width: 767px){
      font-size: 3rem;
    }
  }
  &__prev {
    left: 0px;
  }
  &__next {
    right: 20px;
  }
  &__container {
    @media (max-width: 767px)
    {
        width: 100%;
        max-width: 100%;
        top: 50%;
        margin-top: -140px;
        left: 0;
        right: 0;
        border-radius: 0;
        height: 280px;
    }
    position: absolute;
    overflow: hidden;
    cursor: pointer;
    overflow: hidden;
    max-width: 100vh;
    margin: 0.5rem auto 0;
    left: 0.5rem;
    right: 0.5rem;
    height: 60vh;
    border-radius: $radius-large;
    background-color: #e5e5e5;
    top: 6rem;
    &__img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
    &:hover .caption {
      background: rgb(14 14 14 / 70%);
    }
  }
  &__gallery {
    @media (max-width: 767px)
    {
      display: none;
    }
    overflow-x: hidden;
    overflow-y: hidden;
    position: absolute;
    bottom: 10px;
    margin: auto;
    max-width: 100vh;
    white-space: nowrap;
    left: 0.5rem;
    right: 0.5rem;
    &__title {
      color: $white;
      margin-bottom: 0.5rem;
    }
    &__container {
      overflow: visible;
      display: block;
      height: 100px;
      white-space: nowrap;
      transition: all 200ms ease-in-out;
      width: 100%;
      &__img {
        width: 100px;
        height: 100px;
        object-fit: cover;
        display: inline-block;
        float: none;
        margin-right: 20px;
        cursor: pointer;
        opacity: 0.6;
        border-radius: $radius-medium;
      }
      &__img--active {
        width: 100px;
        display: inline-block;
        float: none;
        opacity: 1;
      }
    }
  }
}

.modal-enter {
  opacity: 0;
}
.modal-leave-active {
  opacity: 0;
}
.caption{
  position: absolute;
  bottom: 0;
  text-align: center;
  width: 100%;
  padding: 15px;
  color: #f6f6f6;
  background: rgb(14 14 14 / 50%);
  span{
    font-size: 1rem;
  }
}
</style>